import { fetchWaitlistCountsThunk, hideWaitlistForm, saveWaitlistThunk } from 'actions/waitlists/WaitlistsActions';
import ClientModel from '@spike/client-model';
import Marketplace from '@spike/marketplace-model';
import { MarketplaceSchedule } from '@spike/model';
import ClientSearch from 'components/CreateBooking/ClientSearch';
import Button from 'components/UI/V3/Button/Button';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { Select } from 'components/UI/V3/Forms/Select/Select';
import WaitlistClientWindow from 'components/Waitlist/WaitlistClientWindow/WaitlistClientWindow';
import WaitlistEditor from 'components/Waitlist/WaitlistEditor/WaitlistEditor';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import AutocompleteResult from 'model/AutocompleteResult';
import moment from 'moment-timezone';
import { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './WaitlistForm.module.scss';
import { buildCreateWaitlistInput, WaitlistFormValues } from './waitlistFormUtils';

interface WaitlistFormProps {
    marketplaceIds: Array<number>;
}

interface WaitlistLocation {
    id: number;
    name: string;
    timeZone: string;
    schedule: MarketplaceSchedule;
}

const EMPTY_VALUES: WaitlistFormValues = {
    pets: [],
    preferredTime: null,
    validUntil: null,
    notes: ''
};

export const WaitlistForm: FunctionComponent<WaitlistFormProps> = ({ marketplaceIds }) => {
    const dispatch = useDispatch();

    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const currentMarketplace = useSelector<RootState, Marketplace>(state => state.marketplace.marketplace);
    const clientResults = useSelector<RootState, Array<AutocompleteResult>>(state => state.autocomplete.results);

    // Locations the staff can see; a single-location business may not have organization data.
    const locations = useMemo<Array<WaitlistLocation>>(() => {
        const organizationLocations = organizationMarketplaces
            .filter(location => location.active)
            .map(location => ({
                id: location.id,
                name: location.name,
                timeZone: location.timeZone,
                schedule: location.schedule
            }));

        return organizationLocations.length > 0 || currentMarketplace.id === undefined
            ? organizationLocations
            : [
                  {
                      id: currentMarketplace.id,
                      name: currentMarketplace.basics.businessName,
                      timeZone: currentMarketplace.schedule.timeZone,
                      schedule: currentMarketplace.schedule
                  }
              ];
    }, [organizationMarketplaces, currentMarketplace]);

    const [values, setValues] = useState<WaitlistFormValues>(() => ({
        ...EMPTY_VALUES,
        marketplaceId: (locations.find(location => location.id === currentMarketplace.id) ?? locations[0])?.id
    }));

    // Name the client was picked by; it must not follow the client store, which ClientSearch reloads
    // whenever its name changes.
    const [clientName, setClientName] = useState<string>();
    // Pet the client was found by (searching by pet name).
    const [suggestedPetId, setSuggestedPetId] = useState<number>();
    const [addingClient, setAddingClient] = useState(false);

    const [errors, setErrors] = useState<Array<string>>([]);
    const [saving, setSaving] = useState(false);

    const location = locations.find(current => current.id === values.marketplaceId);

    const update = (changes: Partial<WaitlistFormValues>) => setValues(current => ({ ...current, ...changes }));

    const changeLocationHandler = (marketplaceId: string) => {
        setClientName(undefined);
        setSuggestedPetId(undefined);
        setValues({ ...EMPTY_VALUES, marketplaceId: Number(marketplaceId) });
    };

    // Add Appointment's client search: 0 means the client was removed. A client found by a pet
    // name gets that pet preselected.
    const changeClientHandler = (customerId: number, name?: string) => {
        const result = clientResults.find(current => current.id === customerId && current.name === name);

        setClientName(customerId > 0 ? name : undefined);
        setSuggestedPetId(result?.type === 'Pet' ? result.petId : undefined);
        update({ customerId: customerId > 0 ? customerId : undefined, pets: [] });
    };

    const savedClientHandler = (client: ClientModel) => {
        setAddingClient(false);
        changeClientHandler(client.id!, `${client.firstName} ${client.lastName}`);
    };

    const serviceCount = values.pets.reduce((count, pet) => count + pet.services.length, 0);
    const isValid = !!values.marketplaceId && !!values.customerId && serviceCount > 0;

    const saveHandler = async () => {
        if (!isValid || !values.marketplaceId) {
            return;
        }

        setSaving(true);
        setErrors([]);

        const result = await (dispatch(
            saveWaitlistThunk(values.marketplaceId, buildCreateWaitlistInput(values))
        ) as unknown as Promise<{ errors: Array<string> }>);

        if (result.errors.length === 0) {
            // The panel moves on to the saved waitlist detail, which unmounts this form.
            dispatch(fetchWaitlistCountsThunk(marketplaceIds));
            return;
        }

        setErrors(result.errors);
        setSaving(false);
    };

    return (
        <WaitlistEditor
            title="Add Waitlist"
            marketplaceId={values.marketplaceId}
            timeZone={location?.timeZone ?? moment.tz.guess()}
            schedule={location?.schedule}
            values={values}
            suggestedPetId={suggestedPetId}
            errors={errors}
            onBack={() => dispatch(hideWaitlistForm())}
            onChange={update}
            renderTop={() => (
                <>
                    <Select
                        label="Location"
                        placeholder="Select a Location"
                        value={values.marketplaceId ? String(values.marketplaceId) : undefined}
                        options={locations.map(current => ({ label: current.name, value: String(current.id) }))}
                        onChange={changeLocationHandler}
                    />

                    <hr className={styles.divider} />

                    <ClientSearch
                        // Starts over when the location changes.
                        key={values.marketplaceId}
                        marketplaceId={values.marketplaceId}
                        parentID={values.customerId}
                        parentName={clientName}
                        setPetParent={changeClientHandler}
                        onAddClient={() => setAddingClient(true)}
                    />

                    {addingClient && (
                        <WaitlistClientWindow
                            onClientSaved={savedClientHandler}
                            onClose={() => setAddingClient(false)}
                        />
                    )}
                </>
            )}
            footer={
                <DrawerFooter>
                    <Button
                        fullWidth
                        size="large"
                        variant="green"
                        label="Add to Waitlist"
                        disabled={!isValid || saving}
                        isLoading={saving}
                        onClick={saveHandler}
                    />
                </DrawerFooter>
            }
        />
    );
};

export default WaitlistForm;
